/**
 * Client-side structured logger
 *
 * Features:
 * - Environment-based log level filtering
 * - Correlation ID propagation
 * - Structured log output in development
 * - Production-ready (can be extended to send logs to backend)
 *
 * Environment variables:
 * - VITE_LOG_LEVEL: Minimum log level (debug, info, warn, error)
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  correlationId?: string;
  [key: string]: unknown;
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: LogContext;
}

const LOG_LEVEL_VALUES: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

// Get minimum log level from environment
const getMinLogLevel = (): LogLevel => {
  const level = import.meta.env.VITE_LOG_LEVEL?.toLowerCase() as LogLevel;
  if (level && LOG_LEVEL_VALUES[level] !== undefined) {
    return level;
  }
  // Default to 'info' in production, 'debug' in development
  return import.meta.env.PROD ? 'info' : 'debug';
};

const minLogLevel = getMinLogLevel();

// Current correlation ID (set from API responses)
let currentCorrelationId: string | undefined;

/**
 * Set the current correlation ID (typically from an API response)
 */
export function setCorrelationId(id: string | undefined): void {
  currentCorrelationId = id;
}

/**
 * Get the current correlation ID
 */
export function getCorrelationId(): string | undefined {
  return currentCorrelationId;
}

/**
 * Check if a log level should be output
 */
function shouldLog(level: LogLevel): boolean {
  return LOG_LEVEL_VALUES[level] >= LOG_LEVEL_VALUES[minLogLevel];
}

/**
 * Format a log entry for console output
 */
function formatLog(entry: LogEntry): string {
  const prefix = `[${entry.timestamp}] [${entry.level.toUpperCase()}]`;
  if (entry.context?.correlationId) {
    return `${prefix} [${entry.context.correlationId}] ${entry.message}`;
  }
  return `${prefix} ${entry.message}`;
}

/**
 * Log a message at the specified level
 */
function log(level: LogLevel, message: string, context?: LogContext): void {
  if (!shouldLog(level)) return;

  const entry: LogEntry = {
    level,
    message,
    timestamp: new Date().toISOString(),
    context: {
      ...context,
      correlationId: context?.correlationId ?? currentCorrelationId,
    },
  };

  // Use appropriate console method
  const consoleMethod = level === 'debug' ? 'log' : level;
  const formattedMessage = formatLog(entry);

  if (context && Object.keys(context).length > 0) {
    console[consoleMethod](formattedMessage, context);
  } else {
    console[consoleMethod](formattedMessage);
  }
}

/**
 * Client-side logger
 */
export const logger = {
  debug: (message: string, context?: LogContext) => log('debug', message, context),
  info: (message: string, context?: LogContext) => log('info', message, context),
  warn: (message: string, context?: LogContext) => log('warn', message, context),
  error: (message: string, context?: LogContext) => log('error', message, context),
};

